import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { ArrowLeft, Mail, ExternalLink, Send, Package, User, MapPin, Calendar, DollarSign, Reply } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";

// -------- helpers --------
const Section = ({ title, children, actions }) => (
  <section className="brutal-static bg-white rounded-2xl p-6 space-y-3">
    <div className="flex items-center justify-between">
      <h2 className="font-display font-black text-xl text-black">{title}</h2>
      {actions}
    </div>
    <div>{children}</div>
  </section>
);

const KV = ({ label, value, mono = false }) => (
  <div className="border-t border-black/10 py-2.5 flex flex-wrap gap-3 items-baseline">
    <div className="text-[10px] tracking-[0.28em] uppercase font-black text-[#EC008C] w-40 shrink-0">{label}</div>
    <div className={`text-sm text-black ${mono ? "font-mono" : "font-light"} break-words`}>
      {value ?? <span className="text-[#999]">-</span>}
    </div>
  </div>
);

const BackHeader = ({ to, title, subtitle, badge }) => (
  <header className="mb-6">
    <Link to={to} className="inline-flex items-center gap-2 text-[11px] tracking-[0.22em] uppercase font-black text-black hover:text-[#EC008C]">
      <ArrowLeft size={14} /> Back to dashboard
    </Link>
    <div className="mt-4 flex flex-wrap items-baseline gap-3">
      <h1 className="font-display font-black text-3xl md:text-4xl text-black">{title}</h1>
      {badge}
    </div>
    {subtitle && <div className="mt-2 text-sm text-[#555]">{subtitle}</div>}
  </header>
);

const useAdminGuard = () => {
  const { user, loading } = useAuth();
  const nav = useNavigate();
  useEffect(() => {
    if (!loading && (!user || user.role !== "admin")) nav("/admin/login");
  }, [user, loading, nav]);
};

// =================================================================
// ORDER DETAIL
// =================================================================
export const AdminOrderDetail = () => {
  useAdminGuard();
  const { id } = useParams();
  const [order, setOrder] = useState(null);
  const [err, setErr] = useState(null);
  const [saving, setSaving] = useState(false);
  const [notifying, setNotifying] = useState(false);

  useEffect(() => {
    api.get(`/admin/orders/${id}`).then((r) => setOrder(r.data)).catch((e) => setErr(e?.response?.data?.detail || "Not found"));
  }, [id]);

  const notifyPickup = async () => {
    setNotifying(true);
    try {
      const { data } = await api.post(`/admin/orders/${id}/ready-for-pickup`);
      setOrder(data);
      toast.success("Customer notified their order is ready for pickup");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to notify customer");
    } finally {
      setNotifying(false);
    }
  };

  const toggleStatus = async (field, value) => {
    setSaving(true);
    try {
      const { data } = await api.put(`/admin/orders/${id}`, { [field]: value });
      setOrder(data);
    } catch (e) {
      // eslint-disable-next-line no-alert
      alert(e?.response?.data?.detail || "Failed to update");
    } finally {
      setSaving(false);
    }
  };

  if (err) return <ErrorState msg={err} />;
  if (!order) return <LoadingState />;

  const addr = (order.shipping_address && typeof order.shipping_address === "object") ? order.shipping_address : {};
  const legacyAddrStr = typeof order.shipping_address === "string" ? order.shipping_address : null;
  const phone = order.customer_phone || addr.phone || "";
  const paid = order.payment_status === "paid";
  return (
    <main data-testid="admin-order-detail" className="max-w-5xl mx-auto px-5 py-8 space-y-5">
      <BackHeader
        to="/admin"
        title={`Order #${order.id.slice(0, 8)}`}
        subtitle={new Date(order.created_at).toLocaleString()}
        badge={
          <span
            data-testid="order-payment-badge"
            className={`px-3 py-1 rounded-full text-[10px] uppercase font-black tracking-widest border-2 border-black ${paid ? "bg-[#CCDE00]" : "bg-[#F3E30A]"}`}
          >
            {order.payment_status}
          </span>
        }
      />

      <Section title="Order Actions">
        <div className="flex flex-wrap gap-3 items-center">
          <button
            data-testid="btn-mark-paid"
            disabled={saving || paid}
            onClick={() => toggleStatus("payment_status", "paid")}
            className="px-4 py-2 border-2 border-black bg-[#CCDE00] text-black text-xs uppercase tracking-wider font-black disabled:opacity-50"
          >
            Mark Paid
          </button>
          <button
            data-testid="btn-mark-unpaid"
            disabled={saving || !paid}
            onClick={() => toggleStatus("payment_status", "pending")}
            className="px-4 py-2 border-2 border-black bg-white text-black text-xs uppercase tracking-wider font-black disabled:opacity-50"
          >
            Mark Unpaid
          </button>
          <button
            data-testid="btn-mark-fulfilled"
            disabled={saving || order.status === "fulfilled"}
            onClick={() => toggleStatus("status", "fulfilled")}
            className="px-4 py-2 border-2 border-black bg-black text-white text-xs uppercase tracking-wider font-black disabled:opacity-50"
          >
            Mark Fulfilled
          </button>
          <span className="text-xs text-[#555]">Fulfillment: <strong>{order.status || "pending"}</strong></span>
        </div>
        {order.has_pickup && (
          <div className="mt-4 pt-4 border-t border-black/10 flex flex-wrap gap-3 items-center">
            <button
              data-testid="btn-ready-for-pickup"
              disabled={notifying}
              onClick={notifyPickup}
              className="px-4 py-2 border-2 border-black bg-[#EC008C] text-white text-xs uppercase tracking-wider font-black disabled:opacity-50"
            >
              {notifying ? "Notifying..." : order.pickup_ready ? "Re-send Pickup Notice" : "Ready for Pickup"}
            </button>
            {order.pickup_ready && (
              <span data-testid="pickup-notified-badge" className="text-xs text-[#0a8a1e] font-bold">
                Customer notified{order.pickup_notified_at ? ` · ${new Date(order.pickup_notified_at).toLocaleString()}` : ""}
              </span>
            )}
            <span className="text-[11px] text-[#555]">This order includes local-pickup items.</span>
          </div>
        )}
      </Section>

      <Section title="Customer">
        <KV label="Name" value={order.customer_name} />
        <KV label="Email" value={<a href={`mailto:${order.customer_email}`} className="text-[#EC008C] underline">{order.customer_email}</a>} />
        <KV label="Phone" value={phone ? <a href={`tel:${phone}`} className="text-[#EC008C] underline">{phone}</a> : ""} />
      </Section>

      <Section title="Shipping Address">
        {legacyAddrStr ? (
          <KV label="Address" value={legacyAddrStr} />
        ) : (
          <>
            <KV label="Street" value={addr.line1} />
            <KV label="Line 2" value={addr.line2} />
            <KV label="City" value={addr.city} />
            <KV label="State" value={addr.state} />
            <KV label="Postal Code" value={addr.postal_code} />
            <KV label="Country" value={addr.country} />
          </>
        )}
      </Section>

      <Section title={`Items (${(order.items || []).length} lines · ${(order.items || []).reduce((s, it) => s + it.quantity, 0)} total qty)`}>
        <div className="space-y-3">
          {(order.items || []).map((it, i) => (
            <div key={i} data-testid={`order-item-${i}`} className="flex gap-4 items-start border border-black/10 rounded-xl p-3 bg-[#FAFAF4]">
              {it.image && <img src={it.image} alt="" className="w-16 h-16 object-cover rounded border border-black" />}
              <div className="flex-1">
                <div className="font-bold text-black">{it.name}</div>
                <div className="text-xs text-[#555] mt-0.5 font-mono">SKU: {(it.product_id || "").slice(0, 8)}</div>
                <div className="text-xs text-[#555] mt-1">
                  Qty: <strong>{it.quantity}</strong> · Unit: ${it.price?.toFixed(2)}
                  {it.color ? ` · Color: ${it.color}` : ""}
                  {it.size ? ` · Size: ${it.size}` : ""}
                </div>
                {it.monogram && (
                  <div data-testid={`order-item-monogram-${i}`} className="mt-1.5 inline-block px-2 py-0.5 rounded-full text-[10px] uppercase tracking-widest font-black bg-[#000000] text-white">
                    Monogram: {it.monogram}{Number(it.monogram_price || 0) > 0 ? ` · +$${Number(it.monogram_price).toFixed(2)}` : " · Free"}
                  </div>
                )}
                {it.fulfillment && (
                  <span className={`inline-block mt-1.5 px-2 py-0.5 rounded-full text-[9px] uppercase tracking-widest font-black border ${it.fulfillment === "pickup" ? "bg-[#EC008C] text-white border-[#EC008C]" : "bg-white text-black border-black"}`}>
                    {it.fulfillment === "pickup" ? "Local Pickup" : "Ship"}
                  </span>
                )}
              </div>
              <div className="font-mono text-sm text-black">${((it.price + Number(it.monogram_price || 0)) * it.quantity).toFixed(2)}</div>
            </div>
          ))}
        </div>
      </Section>

      <Section title="Totals">
        <KV label="Subtotal" value={<span className="font-mono">${order.subtotal?.toFixed(2)}</span>} />
        {order.discount_amount > 0 && <KV label="Discount" value={<span className="font-mono text-[#EC008C]">-${order.discount_amount.toFixed(2)}</span>} />}
        <KV label="Shipping" value={<span className="font-mono">${(order.shipping_cost ?? 0).toFixed(2)}</span>} />
        {order.handling_total > 0 && <KV label="Handling" value={<span className="font-mono">${order.handling_total.toFixed(2)}</span>} />}
        <KV label="Total" value={<span className="font-mono font-black text-lg">${order.total?.toFixed(2)}</span>} />
        <KV label="Stripe Session" value={order.session_id || order.stripe_session_id} mono />
      </Section>

      {(order.processing_terms_snapshot && order.processing_terms_snapshot.length > 0) && (
        <Section title="Processing Terms (agreed at checkout)">
          <div className="text-[10px] uppercase tracking-widest text-[#555] mb-2">
            Buyer acknowledgement: {" "}
            <strong className={order.processing_terms_acknowledged ? "text-[#0a8a1e]" : "text-[#EC008C]"}>
              {order.processing_terms_acknowledged ? "AGREED" : "NOT AGREED"}
            </strong>
          </div>
          <ul className="space-y-1.5 text-xs">
            {order.processing_terms_snapshot.map((t, i) => (
              <li key={i} data-testid={`order-processing-term-${i}`} className="border-b border-black/10 pb-1.5">
                <strong>{t.processing_time}</strong>
                <span className="text-[#666]"> — {t.name}</span>
              </li>
            ))}
          </ul>
        </Section>
      )}
    </main>
  );
};

// =================================================================
// CUSTOM ORDER DETAIL
// =================================================================
export const AdminCustomOrderDetail = () => {
  useAdminGuard();
  const { id } = useParams();
  const [order, setOrder] = useState(null);
  const [err, setErr] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get(`/admin/custom-orders/${id}`).then((r) => setOrder(r.data)).catch((e) => setErr(e?.response?.data?.detail || "Not found"));
  }, [id]);

  const updateStatus = async (status) => {
    setSaving(true);
    try {
      await api.put(`/admin/custom-orders/${id}`, { status });
      setOrder({ ...order, status });
      toast.success("Status updated");
    } catch (e) {
      toast.error("Failed to update");
    } finally {
      setSaving(false);
    }
  };

  if (err) return <ErrorState msg={err} />;
  if (!order) return <LoadingState />;

  const cfg = order.design_config || {};
  const elements = cfg.elements || [];
  const imageElements = elements.filter((el) => el.type === "image" && el.src);
  const textElements = elements.filter((el) => el.type === "text");

  return (
    <main data-testid="admin-custom-detail" className="max-w-5xl mx-auto px-5 py-8 space-y-5">
      <BackHeader
        to="/admin"
        title={`Custom Design #${order.id.slice(0, 8)}`}
        subtitle={new Date(order.created_at).toLocaleString()}
        badge={<span className="px-3 py-1 rounded-full text-[10px] uppercase font-black tracking-widest border-2 border-black bg-[#F3E30A]">{order.status}</span>}
      />

      <Section
        title="Update Status"
        actions={
          <select
            data-testid="custom-status-select"
            value={order.status}
            disabled={saving}
            onChange={(e) => updateStatus(e.target.value)}
            className="border-2 border-black rounded-full px-3 py-1 text-sm font-bold bg-white"
          >
            <option value="submitted">Submitted</option>
            <option value="in-review">In Review</option>
            <option value="approved">Approved</option>
            <option value="in-production">In Production</option>
            <option value="shipped">Shipped</option>
          </select>
        }
      >
        <div className="text-sm text-[#555]">Change fulfillment status to trigger internal workflow.</div>
      </Section>

      <Section title="Customer">
        <KV label="Name" value={order.customer_name} />
        <KV label="Email" value={<a href={`mailto:${order.customer_email}`} className="text-[#EC008C] underline">{order.customer_email}</a>} />
        <KV label="Phone" value={order.customer_phone} />
      </Section>

      <Section title="Product">
        <KV label="Base Product" value={order.product_type || cfg.product} />
        <KV label="Base Color" value={cfg.baseColor || order.base_color} />
        <KV label="Quantity" value={order.quantity ?? cfg.quantity} />
        <KV label="Notes" value={<span className="whitespace-pre-wrap">{order.notes}</span>} />
      </Section>

      {imageElements.length > 0 && (
        <Section title={`Uploaded Artwork (${imageElements.length})`}>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {imageElements.map((el, i) => (
              <a
                key={i}
                href={el.src}
                target="_blank"
                rel="noreferrer"
                data-testid={`custom-artwork-${i}`}
                className="block border-2 border-black rounded-lg overflow-hidden bg-[#F7F7F5] aspect-square group"
              >
                <img src={el.src} alt={`Artwork ${i + 1}`} className="w-full h-full object-contain group-hover:scale-105 transition-transform" />
              </a>
            ))}
          </div>
          <div className="mt-3 text-xs text-[#555]">Click any thumbnail to open the full-size uploaded image.</div>
        </Section>
      )}

      {textElements.length > 0 && (
        <Section title={`Text on Design (${textElements.length})`}>
          <div className="space-y-2">
            {textElements.map((el, i) => (
              <div key={i} className="border-l-4 border-[#EC008C] pl-4 py-2 bg-[#FAFAF4]">
                <div className="text-lg font-display font-black text-black" style={{ color: el.color, fontFamily: el.fontFamily }}>{el.text}</div>
                <div className="text-[10px] uppercase tracking-widest text-[#555] mt-1">
                  {el.color && <>Color: {el.color} · </>} {el.fontFamily && <>Font: {el.fontFamily}</>}
                </div>
              </div>
            ))}
          </div>
        </Section>
      )}

      {order.design_preview && order.design_preview.startsWith("data:image") && (
        <Section title="Design Preview">
          <img src={order.design_preview} alt="Design preview" className="w-full max-w-xl border-2 border-black rounded-lg" />
        </Section>
      )}
    </main>
  );
};

// =================================================================
// BULK QUOTE DETAIL
// =================================================================
export const AdminBulkQuoteDetail = () => {
  useAdminGuard();
  const { id } = useParams();
  const [q, setQ] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    api.get(`/admin/bulk-quotes/${id}`).then((r) => setQ(r.data)).catch((e) => setErr(e?.response?.data?.detail || "Not found"));
  }, [id]);

  if (err) return <ErrorState msg={err} />;
  if (!q) return <LoadingState />;

  return (
    <main data-testid="admin-bulk-detail" className="max-w-5xl mx-auto px-5 py-8 space-y-5">
      <BackHeader
        to="/admin"
        title={q.company || q.name}
        subtitle={`Submitted ${new Date(q.created_at).toLocaleString()}`}
        badge={<span className="px-3 py-1 rounded-full text-[10px] uppercase font-black tracking-widest border-2 border-black bg-[#DCE842]">{q.service}</span>}
      />

      <Section
        title="Reply Options"
        actions={
          <a
            href={`mailto:${q.email}?subject=Re: Your MAKEBA bulk quote request&body=Hi ${encodeURIComponent(q.name)},%0D%0A%0D%0A`}
            className="inline-flex items-center gap-2 bg-[#EC008C] text-white px-4 py-2 rounded-full text-xs font-bold border-2 border-[#EC008C] hover:bg-black hover:border-black"
          >
            <Mail size={14} /> Email {q.name}
          </a>
        }
      >
        <KV label="Company" value={q.company} />
        <KV label="Contact Name" value={q.name} />
        <KV label="Email" value={<a href={`mailto:${q.email}`} className="text-[#EC008C] underline">{q.email}</a>} />
        <KV label="Phone" value={q.phone} />
      </Section>

      <Section title="Project Specs">
        <KV label="Service" value={q.service} />
        <KV label="Garment Type" value={q.garment_type} />
        <KV label="Quantity" value={q.quantity} />
        <KV label="Print Locations" value={(q.print_locations || []).join(", ")} />
        <KV label="Deadline" value={q.deadline} />
        <KV label="Budget" value={q.budget} />
        <KV label="Notes" value={<span className="whitespace-pre-wrap">{q.notes}</span>} />
      </Section>

      {q.artwork_url && (
        <Section title="Attached Artwork">
          <a
            href={q.artwork_url}
            target="_blank"
            rel="noreferrer"
            data-testid="bulk-artwork-link"
            className="inline-flex items-center gap-2 border-2 border-black rounded-full px-5 py-3 font-bold text-sm bg-[#F3E30A] hover:bg-black hover:text-white transition-colors"
          >
            <ExternalLink size={16} /> Open Artwork Link
          </a>
          <div className="mt-3 text-xs text-[#555] break-all">{q.artwork_url}</div>
        </Section>
      )}
    </main>
  );
};

// =================================================================
// MESSAGE DETAIL + REPLY
// =================================================================
export const AdminMessageDetail = () => {
  useAdminGuard();
  const { id } = useParams();
  const [m, setM] = useState(null);
  const [err, setErr] = useState(null);
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);

  useEffect(() => {
    api.get(`/admin/messages/${id}`).then((r) => {
      setM(r.data);
      setSubject(`Re: your message to MAKEBA Design & Prints`);
      setBody(`Hi ${r.data.name},\n\nThanks for reaching out to MAKEBA Design & Prints. `);
    }).catch((e) => setErr(e?.response?.data?.detail || "Not found"));
  }, [id]);

  const sendReply = async (e) => {
    e.preventDefault();
    if (!subject.trim() || !body.trim()) {
      toast.error("Subject and body are required");
      return;
    }
    setSending(true);
    try {
      const r = await api.post(`/admin/messages/${id}/reply`, { subject, body });
      toast.success("Reply sent");
      const newReplies = [...(m.replies || []), r.data.reply];
      setM({ ...m, replies: newReplies, status: "replied" });
      setBody(`Hi ${m.name},\n\n`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to send");
    } finally {
      setSending(false);
    }
  };

  if (err) return <ErrorState msg={err} />;
  if (!m) return <LoadingState />;

  return (
    <main data-testid="admin-message-detail" className="max-w-5xl mx-auto px-5 py-8 space-y-5">
      <BackHeader
        to="/admin"
        title={`Message from ${m.name}`}
        subtitle={new Date(m.created_at).toLocaleString()}
        badge={
          <span className={`px-3 py-1 rounded-full text-[10px] uppercase font-black tracking-widest border-2 border-black ${m.status === "replied" ? "bg-[#CCDE00]" : "bg-[#F3E30A]"}`}>
            {m.status || "new"}
          </span>
        }
      />

      <Section title="From">
        <KV label="Name" value={m.name} />
        <KV label="Email" value={<a href={`mailto:${m.email}`} className="text-[#EC008C] underline">{m.email}</a>} />
      </Section>

      <Section title="Original Message">
        <div className="whitespace-pre-wrap text-sm text-black leading-relaxed bg-[#FAFAF4] border-l-4 border-[#EC008C] p-4 rounded-r-lg">
          {m.message}
        </div>
      </Section>

      {(m.replies || []).length > 0 && (
        <Section title={`Reply Thread (${m.replies.length})`}>
          <div className="space-y-3">
            {m.replies.map((rep, i) => (
              <div key={i} className="border-2 border-[#CCDE00] rounded-lg p-4 bg-white">
                <div className="flex items-baseline justify-between mb-2 flex-wrap gap-2">
                  <div className="font-bold text-sm text-black">{rep.subject}</div>
                  <div className="text-xs text-[#555]">{new Date(rep.sent_at).toLocaleString()}</div>
                </div>
                <div className="whitespace-pre-wrap text-sm text-black">{rep.body}</div>
              </div>
            ))}
          </div>
        </Section>
      )}

      <Section
        title="Send Reply"
        actions={<Reply size={20} className="text-[#EC008C]" />}
      >
        <form onSubmit={sendReply} className="space-y-3">
          <div>
            <label className="text-[10px] tracking-[0.28em] uppercase font-black text-black mb-1 block">Subject</label>
            <input
              data-testid="reply-subject"
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              required
              className="w-full border-2 border-black rounded-xl px-4 py-3 text-sm bg-white"
            />
          </div>
          <div>
            <label className="text-[10px] tracking-[0.28em] uppercase font-black text-black mb-1 block">Message</label>
            <textarea
              data-testid="reply-body"
              value={body}
              onChange={(e) => setBody(e.target.value)}
              required
              rows={8}
              className="w-full border-2 border-black rounded-xl px-4 py-3 text-sm bg-white font-light leading-relaxed"
            />
          </div>
          <div className="text-xs text-[#555]">
            Sends from your Resend-configured domain. The customer will receive an email at <strong>{m.email}</strong>.
          </div>
          <button
            data-testid="reply-send"
            type="submit"
            disabled={sending}
            className="inline-flex items-center gap-2 bg-[#EC008C] text-white px-6 py-3 rounded-full font-bold text-sm border-2 border-[#EC008C] hover:bg-black hover:border-black transition-colors disabled:opacity-60"
          >
            <Send size={14} /> {sending ? "Sending..." : "Send Reply"}
          </button>
        </form>
      </Section>
    </main>
  );
};

// -------- shared placeholders --------
const LoadingState = () => (
  <main className="max-w-5xl mx-auto px-5 py-16 text-center text-[#555]">Loading...</main>
);
const ErrorState = ({ msg }) => (
  <main className="max-w-5xl mx-auto px-5 py-16">
    <Link to="/admin" className="inline-flex items-center gap-2 text-sm font-bold text-black hover:text-[#EC008C]">
      <ArrowLeft size={14} /> Back
    </Link>
    <div className="mt-6 text-lg text-black">{msg}</div>
  </main>
);
